/**
 * Accounts list — `accounts.html` ported.
 *
 * The prototype's grid is the primary view; the `.seg` control's list button is
 * a real second view here (a `DataTable`, reusing the columns the old table
 * version of this screen already had) rather than the prototype's own
 * decoration — its toggle there has no wiring behind it at all.
 *
 * The segment stat strip is real data, not the prototype's fixed four tiles:
 * `by_segment` groups on `crm.account.segment`, which is free text, so however
 * many distinct segments an org actually uses is however many tiles render
 * (capped at three, by revenue), plus one fixed "At risk / dormant" tile from
 * `risk_exposure`.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useCursorPages } from '../../hooks/useCursorPages.js';
import {
  Card, Badge, SearchInput, ChipSelect, MockBadge, Avatar, ErrorState, EmptyState,
} from '../../components/common/index.jsx';
import { Spinner } from '../../components/common/Loading.jsx';
import { DataTable } from '../../components/common/DataTable.jsx';
import { isMock } from '../../api/mockFallback.js';
import { moneyShort, money, number, date } from '../../utils/formatters.js';
import { useAccounts, useAccountOptions, useCreateAccount } from './useAccounts.js';
import { toRow, healthTone, STAGE_PILL, logoColor } from './accountShape.js';
import { AccountFormModal, NotBuiltModal } from './AccountModals.jsx';
import { EmailComposerModal } from '../emailTemplates/EmailComposerModal.jsx';

const LIFECYCLE_STAGE = ['Prospect', 'Customer', 'Renewal', 'Churned', 'Dormant'];

/** `email` used to live here — §8 is real now, so the card's icon opens `EmailComposerModal`. */
const NOT_BUILT = {
  hierarchy: {
    title: 'Org-wide hierarchy is not available',
    why: 'Every account’s own parent/child tree is real — open one and use its Hierarchy card. Browsing the whole organisation’s hierarchy from this list needs a different endpoint than GET /accounts/{id}/hierarchy, which is scoped to one account, and that browser is not built.',
    endpoint: 'GET /accounts/{id}/hierarchy',
    section: 'docs/API.md §5',
  },
};

export default function AccountsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState('grid');
  const [search, setSearch] = useState('');
  const [industry, setIndustry] = useState('');
  const [territory, setTerritory] = useState('');
  const [stageFilter, setStageFilter] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [notBuilt, setNotBuilt] = useState(null);
  const [emailAccount, setEmailAccount] = useState(null);
  const [gridRows, setGridRows] = useState([]);

  const debounced = useDebounce(search);
  const pages = useCursorPages(24);
  const options = useAccountOptions();
  const createMutation = useCreateAccount();

  const params = useMemo(
    () => ({
      ...pages.params,
      q: debounced || undefined,
      industry_id: industry || undefined,
      territory_id: territory || undefined,
      lifecycle_stage: stageFilter.length ? stageFilter.join(',') : undefined,
      with_stats: true,
    }),
    [pages.params, debounced, industry, territory, stageFilter],
  );

  const { data, isLoading, error, refetch } = useAccounts(params);

  const onFilter = (setter) => (value) => setter(value);
  useEffect(() => { pages.reset(); }, [debounced, industry, territory, stageFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Grid mode accumulates pages under "Load more"; table mode pages normally. */
  useEffect(() => {
    if (!data) return;
    setGridRows((prev) => (pages.cursor ? [...prev, ...data.data] : data.data));
  }, [data, pages.cursor]);

  const switchView = (next) => { setView(next); pages.reset(); };

  const rows = (data?.data ?? []).map(toRow);
  const gridDisplayRows = gridRows.map(toRow);
  const agg = data?.aggregates;
  const total = data?.page?.total ?? 0;
  const customersCount = agg?.by_stage?.Customer ?? 0;

  const toggleStage = (stage) => {
    setStageFilter((s) => (s.includes(stage) ? s.filter((x) => x !== stage) : [...s, stage]));
  };

  const columns = [
    {
      key: 'name', header: 'Account',
      render: (a) => (
        <div>
          <div className="fw-600">{a.name}</div>
          <div className="t-xs text-muted">{a.industry ?? '—'} · {a.billingAddress?.country ?? '—'}</div>
        </div>
      ),
    },
    { key: 'stage', header: 'Stage', width: 110, render: (a) => <Badge tone="neutral">{a.lifecycleStage}</Badge> },
    { key: 'segment', header: 'Segment', width: 120, render: (a) => a.segment ?? <span className="text-muted">—</span> },
    { key: 'revenue', header: 'Revenue', width: 120, align: 'end', render: (a) => (a.revenue ? money(a.revenue) : <span className="text-muted">—</span>) },
    { key: 'openDeals', header: 'Open deals', width: 100, align: 'end', render: (a) => number(a.stats?.open_opportunities) },
    { key: 'contacts', header: 'Contacts', width: 90, align: 'end', render: (a) => number(a.stats?.contacts) },
    {
      key: 'health', header: 'Health', width: 110,
      render: (a) => (a.healthBand ? <Badge tone="neutral">{a.healthBand}</Badge> : <span className="text-muted">—</span>),
    },
    { key: 'owner', header: 'Owner', width: 150, render: (a) => a.owner?.full_name ?? <span className="text-muted">Unowned</span> },
    {
      key: 'lastActivity', header: 'Last activity', width: 130,
      render: (a) => <span className="t-xs text-muted">{a.lastActivityAt ? date(a.lastActivityAt) : '—'}</span>,
    },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumb"><a href="#sell">Sell</a><i className="bi bi-chevron-right" style={{ fontSize: 9 }} aria-hidden="true" /><span>Accounts</span></div>
          <h1 className="page-title">Accounts</h1>
          <p className="page-sub">
            {number(total)} customer organisation{total === 1 ? '' : 's'} · {number(customersCount)} active customers
            {isMock(data) && <MockBadge section="accounts" />}
          </p>
        </div>
        <div className="ms-auto d-flex flex-wrap gap-2">
          <div className="seg">
            <button className={view === 'grid' ? 'active' : ''} onClick={() => switchView('grid')} type="button" title="Grid view">
              <i className="bi bi-grid-3x3-gap" aria-hidden="true" />
            </button>
            <button className={view === 'list' ? 'active' : ''} onClick={() => switchView('list')} type="button" title="List view">
              <i className="bi bi-list-ul" aria-hidden="true" />
            </button>
          </div>
          <button type="button" className="btn btn-soft btn-sm-x" onClick={() => setNotBuilt(NOT_BUILT.hierarchy)}>
            <i className="bi bi-diagram-3 me-1" aria-hidden="true" />Hierarchy
          </button>
          {can('accounts', 'create') && (
            <button type="button" className="btn btn-add btn-sm-x" onClick={() => setCreateOpen(true)}>
              <i className="bi bi-plus-lg me-1" aria-hidden="true" />New account
            </button>
          )}
        </div>
      </div>

      {/* ---- Segment strip ---- */}
      <div className="row g-3 mb-3">
        {(agg?.by_segment ?? []).slice(0, 3).map(({ segment, count, revenue }, i) => {
          const pct = agg.total_revenue ? Math.round((revenue / agg.total_revenue) * 100) : 0;
          const tints = ['#eef2ff', '#f0f9ff', '#ecfdf5'];
          return (
            <div className="col-6 col-lg-3" key={segment}>
              <div className="stat fade-up" style={{ '--tint': tints[i % tints.length] }}>
                <div className="stat-label">{segment}</div>
                <div className="stat-value t-xl mt-1">{number(count)}</div>
                <div className="stat-meta mt-1">{moneyShort(revenue)} ARR · {pct}% of revenue</div>
              </div>
            </div>
          );
        })}
        {agg && (
          <div className="col-6 col-lg-3">
            <div className="stat fade-up" style={{ '--tint': '#fff1f2' }}>
              <div className="stat-label">At risk / dormant</div>
              <div className="stat-value t-xl mt-1">{number(agg.risk_exposure?.count ?? 0)}</div>
              <div className="stat-meta mt-1">
                <span className="pill pill-rose">{moneyShort(agg.risk_exposure?.revenue ?? 0)} exposure</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <SearchInput value={search} onChange={onFilter(setSearch)} placeholder="Search accounts…" />
        <ChipSelect label="Industry" allLabel="All industries" value={industry} onChange={onFilter(setIndustry)} options={options.industries} />
        <ChipSelect label="Region" allLabel="All regions" value={territory} onChange={onFilter(setTerritory)} options={options.territories} />
        {LIFECYCLE_STAGE.map((s) => (
          <button
            key={s} type="button" className={`chip ${stageFilter.includes(s) ? 'active' : ''}`}
            onClick={() => toggleStage(s)}
          >
            {s}
          </button>
        ))}
      </div>

      {view === 'grid' ? (
        isLoading && !gridDisplayRows.length ? (
          <div className="p-4 text-center"><Spinner /></div>
        ) : error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : gridDisplayRows.length === 0 ? (
          <EmptyState icon="bi-building" title="No accounts match" message="Try a different search or filter." />
        ) : (
          <>
            <div className="row g-3">
              {gridDisplayRows.map((a) => <AccountCard key={a.id} account={a} onOpen={() => navigate(`/accounts/${a.id}`)} onEmail={() => setEmailAccount(a)} />)}
            </div>
            {data?.page?.has_more && (
              <div className="d-flex justify-content-center mt-4">
                <button type="button" className="btn btn-soft" disabled={isLoading} onClick={() => pages.goNext(data.page.next_cursor)}>
                  {isLoading ? <Spinner size={16} /> : <>Load more accounts <i className="bi bi-arrow-down ms-1" aria-hidden="true" /></>}
                </button>
              </div>
            )}
          </>
        )
      ) : (
        <Card bodyClass="p-0">
          <DataTable
            columns={columns}
            rows={rows}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            onRowClick={(a) => navigate(`/accounts/${a.id}`)}
            page={data?.page}
            onNext={pages.goNext}
            onPrevious={pages.goPrevious}
            canGoPrevious={pages.canGoPrevious}
            emptyTitle="No accounts match these filters"
          />
        </Card>
      )}

      <AccountFormModal
        open={createOpen}
        account={null}
        options={options}
        parentOptions={(gridDisplayRows.length ? gridDisplayRows : rows).map((a) => ({ value: a.id, label: a.name }))}
        busy={createMutation.isPending}
        onClose={() => setCreateOpen(false)}
        onSubmit={async (payload) => {
          const created = await createMutation.mutateAsync(payload);
          setCreateOpen(false);
          navigate(`/accounts/${created.id}`);
        }}
      />

      <NotBuiltModal open={Boolean(notBuilt)} action={notBuilt} onClose={() => setNotBuilt(null)} />

      <EmailComposerModal
        open={Boolean(emailAccount)}
        onClose={() => setEmailAccount(null)}
        anchor={{ account_id: emailAccount?.id }}
      />
    </>
  );
}

function AccountCard({ account: a, onOpen, onEmail }) {
  const [bg, fg] = healthTone(a.healthBand);
  const colour = logoColor(a.id);
  return (
    <div className="col-md-6 col-xl-4">
      <div className="card-x acct-card hoverable h-100 fade-up">
        <div className="cover" style={{ background: `linear-gradient(120deg, ${colour}, ${colour}bb)` }} />
        <div className="card-body-x pt-0">
          <div className="d-flex align-items-end justify-content-between">
            <div className="acct-logo" style={{ background: colour }}>{a.name.slice(0, 2).toUpperCase()}</div>
            {a.healthScore != null && (
              <div className="health-ring" style={{ background: bg, color: fg }} title={`Customer health ${a.healthScore}`}>{a.healthScore}</div>
            )}
          </div>
          <div className="mt-2">
            <a href={`/accounts/${a.id}`} className="t-md fw-650 ink d-block text-truncate"
               onClick={(e) => { e.preventDefault(); onOpen(); }}>{a.name}</a>
            <div className="t-xs ink-3">{a.industry ?? '—'}</div>
          </div>
          <div className="d-flex flex-wrap gap-1 mt-2">
            <span className={`pill ${STAGE_PILL[a.lifecycleStage] ?? 'pill-gray'}`}>{a.lifecycleStage}</span>
            {a.territory?.name && <span className="pill pill-gray"><i className="bi bi-geo-alt-fill" aria-hidden="true" />{a.territory.name}</span>}
            {a.healthBand && <span className="pill" style={{ background: bg, color: fg, borderColor: 'transparent' }}>{a.healthBand}</span>}
          </div>
          <hr className="soft" />
          <div className="row g-2 t-xs">
            <div className="col-6"><div className="ink-4 t-2xs">Annual revenue</div><div className="fw-750 t-sm tabular">{a.revenue ? moneyShort(a.revenue) : '—'}</div></div>
            <div className="col-6"><div className="ink-4 t-2xs">Customer since</div><div className="fw-750 t-sm">{a.customerSince ? date(a.customerSince) : '—'}</div></div>
            <div className="col-6"><div className="ink-4 t-2xs">Contacts</div><div className="fw-750 t-sm">{a.stats?.contacts ?? 0}</div></div>
            <div className="col-6"><div className="ink-4 t-2xs">Open deals</div><div className="fw-750 t-sm">{a.stats?.open_opportunities ?? 0}</div></div>
          </div>
          <hr className="soft" />
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <Avatar name={a.owner?.full_name ?? '?'} size={22} />
              <span className="t-xs ink-3">{a.owner?.full_name ?? 'Unowned'}</span>
            </div>
            <div className="d-flex gap-1">
              {a.phone
                ? <a className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }} title="Call" href={`tel:${a.phone}`}><i className="bi bi-telephone" aria-hidden="true" /></a>
                : <button type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }} title="Call" disabled><i className="bi bi-telephone" aria-hidden="true" /></button>}
              <button type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }} title="Email" onClick={onEmail}><i className="bi bi-envelope" aria-hidden="true" /></button>
              <button type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }} title="Open" onClick={onOpen}><i className="bi bi-box-arrow-up-right" aria-hidden="true" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
